import { saveAs } from 'file-saver'
import { BASE_URL } from './httpClient'

/**
 * 导出页面指定区域为 PDF
 */
export async function exportToPdf(elementId: string, filename: string) {
  const element = document.getElementById(elementId)
  if (!element) return

  // 动态导入重型库
  const [html2canvas, { jsPDF }] = await Promise.all([
    import('html2canvas').then(m => m.default),
    import('jspdf')
  ])

  const originalBoxShadow = element.style.boxShadow
  const originalOverflow = element.style.overflow
  element.style.boxShadow = 'none'
  element.style.overflow = 'visible'

  // 等待一段时间，确保图片（如印章）加载并渲染完成
  await new Promise(resolve => setTimeout(resolve, 500))

  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    width: element.scrollWidth,
    height: element.scrollHeight,
    windowWidth: element.scrollWidth,
    windowHeight: element.scrollHeight,
    logging: false
  })

  element.style.boxShadow = originalBoxShadow
  element.style.overflow = originalOverflow

  const pageWidth = 595.28
  const pageHeight = 841.89
  const imgWidth = pageWidth
  const imgHeight = (pageWidth / canvas.width) * canvas.height

  const pageData = canvas.toDataURL('image/jpeg', 1.0)
  // jsPDF uses a lowercase constructor name in its public API.
  // eslint-disable-next-line new-cap
  const pdf = new jsPDF('p', 'pt', 'a4')

  if (imgHeight <= pageHeight) {
    pdf.addImage(pageData, 'JPEG', 0, 0, imgWidth, imgHeight)
  } else {
    let leftHeight = imgHeight
    let position = 0
    while (leftHeight > 0.5) {
      pdf.addImage(pageData, 'JPEG', 0, position, imgWidth, imgHeight)
      leftHeight -= pageHeight
      position -= pageHeight
      if (leftHeight > 0.5) {
        pdf.addPage()
      }
    }
  }

  pdf.save(`${filename}.pdf`)
}

/**
 * 编程式构建 Excel 导出，完全还原 UI 布局与字段，并包含印章图片
 */
export async function exportQuotationToExcel(detail: any, filename: string, templateId: number) {
  // 动态导入重型库
  const ExcelJS = await import('exceljs').then(m => m.default)

  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet('报价单')

  // A4 宽度优化：总宽度控制在约 100 左右，确保不缩放即可打印
  worksheet.columns = [
    { width: 6 }, // 序号
    { width: 22 }, // 名称
    { width: 22 }, // 型号
    { width: 6 }, // 单位
    { width: 6 }, // 数量
    { width: 13 }, // 单价
    { width: 13 }, // 总价
    { width: 12 } // 备注
  ]

  // 设置打印区域与页面选项
  worksheet.pageSetup = {
    paperSize: 9, // A4
    orientation: 'portrait',
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    margins: {
      left: 0.2,
      right: 0.2,
      top: 0.5,
      bottom: 0.5,
      header: 0.3,
      footer: 0.3
    }
  }

  // 设置全局默认样式：全部居中，防止拥挤
  worksheet.eachRow(row => {
    row.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true }
  })

  const titleRow = worksheet.addRow([detail.titleDetail?.name || ''])
  titleRow.font = { size: 18, bold: true }
  titleRow.alignment = { horizontal: 'center', vertical: 'middle' }
  titleRow.height = 40
  worksheet.mergeCells(1, 1, 1, 8)

  const subTitleRow = worksheet.addRow([templateId === 1 ? '销售合同' : '维修报价单'])
  subTitleRow.font = { size: 15, bold: true }
  subTitleRow.alignment = { horizontal: 'center', vertical: 'middle' }
  subTitleRow.height = 35
  worksheet.mergeCells(2, 1, 2, 8)

  worksheet.addRow([])

  const biz = detail.details?.[0] || {}
  const leftItems = [
    `TO 收件人：${detail.customerCompanyContactDetail?.name || ''}`,
    `E-mail 电邮：${detail.receivingEmail || detail.customerCompanyContactDetail?.receivingEmail || ''}`,
    `Company 公司：${detail.customerCompanyDetail?.name || ''}`,
    `Tel 电话：${detail.customerCompanyContactDetail?.phone || ''}`,
    `Project 报价项目：${biz.businessName || ''}`,
    `Model 型号：${biz.businessModel || ''}`
  ]
  const rightItems = [
    `Quote From 报价人：${detail.salesManDetail?.name || detail.titleDetail?.name || ''}`,
    `Tel 电话：${detail.salesManDetail?.phone || ''}`,
    `Line 座机：${detail.titleDetail?.phone || ''}`,
    `E-Mail 电邮：${detail.salesManDetail?.email || ''}`,
    `Date 日期：${detail.createTime ? detail.createTime.slice(0, 10) : new Date().toLocaleDateString().replace(/\//g, '-')}`,
    `Quote No. 业务编号：${biz.businessNo || detail.no || ''}`
  ]

  // 基础信息部分：合并单元格，高度增加
  for (let i = 0; i < leftItems.length; i++) {
    const row = worksheet.addRow([leftItems[i], '', '', rightItems[i], '', '', '', ''])
    worksheet.mergeCells(row.number, 1, row.number, 3)
    worksheet.mergeCells(row.number, 4, row.number, 8)

    row.eachCell(cell => {
      cell.border = {}
      cell.alignment = { vertical: 'middle', wrapText: true, horizontal: 'left' } // 信息类保持靠左更易读
      cell.font = { size: 10 }
    })

    row.height = 30 // 增加行高，减少拥挤感
  }

  worksheet.addRow([])

  // 仅在“含检测报告”的模板（ID 3 和 4）中显示工作描述部分
  if ([3, 4].includes(Number(templateId)) && (detail.fault || detail.checkDes)) {
    const descHeader = worksheet.addRow(['工作描述：'])
    descHeader.font = { bold: true }
    descHeader.alignment = { horizontal: 'left', vertical: 'middle' }
    descHeader.height = 25

    const fRow = worksheet.addRow(['设备故障现象：', detail.fault || ''])
    worksheet.mergeCells(fRow.number, 2, fRow.number, 8)
    fRow.alignment = { wrapText: true, vertical: 'middle', horizontal: 'left' }
    fRow.height = 35

    const cRow = worksheet.addRow(['初步检测情况：', detail.checkDes || ''])
    worksheet.mergeCells(cRow.number, 2, cRow.number, 8)
    cRow.alignment = { wrapText: true, vertical: 'middle', horizontal: 'left' }
    cRow.height = 35

    const pRow = worksheet.addRow(['损坏元件：', detail.changeParts || ''])
    worksheet.mergeCells(pRow.number, 2, pRow.number, 8)
    pRow.alignment = { wrapText: true, vertical: 'middle', horizontal: 'left' }
    pRow.height = 35

    worksheet.addRow([])
  }

  const headerRow = worksheet.addRow(['序号', '名称', '型号', '单位', '数量', '单价', '总价', '备注'])
  headerRow.height = 35
  headerRow.eachCell(cell => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' }}
    cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }}
    cell.font = { bold: true, size: 11 }
    cell.alignment = { horizontal: 'center', vertical: 'middle' }
  })

  detail.details?.forEach((item: any, index: number) => {
    const row = worksheet.addRow([
      index + 1,
      item.businessName || '',
      item.businessModel || '',
      item.unit || item.businessUnit || '件',
      item.amount || item.businessAmount || 0,
      parseFloat(item.price || 0),
      parseFloat(item.price || 0) * parseFloat(item.amount || item.businessAmount || 0),
      item.mark || ''
    ])
    row.height = 35 // 增加明细行高
    row.eachCell(cell => {
      cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }}
      cell.alignment = { wrapText: true, vertical: 'middle', horizontal: 'center' }
    })
    // 价格格式：两位小数
    row.getCell(6).numFmt = '#,##0.00'
    row.getCell(7).numFmt = '#,##0.00'
  })

  worksheet.addRow([])

  const memoTitle = worksheet.addRow(['备注：'])
  memoTitle.font = { bold: true }
  memoTitle.alignment = { horizontal: 'left' }
  const memoContent = worksheet.addRow([detail.quotationMarkTemplateDetail?.mark || ''])
  memoContent.alignment = { wrapText: true, vertical: 'top', horizontal: 'left' }
  memoContent.height = 100 // 显著增加备注高度，填补下方空白
  worksheet.mergeCells(memoContent.number, 1, memoContent.number + 3, 8)

  worksheet.addRow([])
  const isContract = Number(templateId) === 1
  const labelLeft = isContract ? '需方签字盖章：' : '客户代表方签字：'
  const labelRight = isContract ? '供方签字盖章：' : '业务签字：'

  const signRow = worksheet.addRow([`${labelLeft}____________________`, '', '', '', `${labelRight}____________________`, '', '', ''])
  worksheet.mergeCells(signRow.number, 1, signRow.number, 4)
  worksheet.mergeCells(signRow.number, 5, signRow.number, 8)
  signRow.height = 60 // 适度增加签字区高度
  signRow.alignment = { vertical: 'bottom', horizontal: 'center' }

  // 尝试获取并添加印章图片
  if (detail.titleDetail?.sealImg) {
    try {
      const sealUrl = BASE_URL + (detail.titleDetail.sealImg.startsWith('/') ? detail.titleDetail.sealImg : '/' + detail.titleDetail.sealImg)
      const response = await fetch(sealUrl, { credentials: 'include' })
      if (response.ok) {
        const arrayBuffer = await response.arrayBuffer()
        const imageId = workbook.addImage({
          buffer: arrayBuffer,
          extension: 'png'
        })
        worksheet.addImage(imageId, {
          tl: { col: 6.1, row: signRow.number - 1 + 0.3 },
          ext: { width: 100, height: 100 }
        })
      }
    } catch (e) {
      console.error('Failed to embed seal image into Excel', e)
    }
  }

  // 显式设置打印区域
  worksheet.pageSetup.printArea = `A1:H${signRow.number + 1}`

  const buffer = await workbook.xlsx.writeBuffer()
  saveAs(new Blob([buffer]), `${filename}.xlsx`)
}
